import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { CandidateMargin, MarginBand } from './suggestionsApi';
import { marginCaveat, tollsSentences, type RouteFigure } from './routeFigure';
import { useFeature } from '../featureFlags/FeatureGate';
import { costSplitSentences, FEATURE_DETAILS } from './recommendationView';

const pln = (v: number) => `${v.toLocaleString('pl-PL')} zł`;
const pct = (v: number) => `${v.toLocaleString('pl-PL')} %`;
/** Spacje nierozdzielające — kwota i procent nie łamią się w środku. */
const nb = (s: string) => s.replace(/ /g, ' ');

/** Nazwa i kolor pasma alertu marży. */
export const MARGIN_BAND_CHIP: Record<
  MarginBand,
  { label: string; color: 'success' | 'info' | 'warning' | 'error' }
> = {
  high: { label: 'wysoka', color: 'success' },
  medium: { label: 'średnia', color: 'info' },
  breakEven: { label: '±0', color: 'warning' },
  clearlyNegative: { label: 'wyraźnie ujemna', color: 'error' },
  // Pięć pasm od kosztu (paczka p. Mariusza 21.10, S-21.10-18).
  criticalLoss: { label: 'strata krytyczna', color: 'error' },
  loss: { label: 'strata', color: 'error' },
  minimum: { label: 'marża minimalna', color: 'warning' },
  normal: { label: 'marża normalna', color: 'success' },
  verify: { label: 'do weryfikacji', color: 'info' },
};

/**
 * Chip pasma w wąskiej kolumnie (`wrap`, karta pojazdu): napis może zejść do drugiej linii i chip nie jest
 * szerszy niż komórka (02.11.2026 — „strata krytyczna” przy 1366 px rozpychała tabelę rekomendowanych zleceń
 * o 28 px na stagingu; MUI domyślnie nie łamie napisu chipa).
 */
export const WRAP_CHIP_SX = {
  height: 'auto',
  minHeight: 18,
  maxWidth: '100%',
  '& .MuiChip-label': { px: 0.75, fontSize: 11, whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.3 },
} as const;

/** Dopisek w dymku, gdy pasma nie ma. */
export const NO_BAND_NOTE =
  'Brak alertu marży: progi nie są jeszcze ustawione albo marża mieści się między nimi. Progi ustawia sekcja administracyjna w parametrach rekomendacji.';

/**
 * Zdanie o pasmie w dymku. Pięć pasm (21.10.2026, S-21.10-18) niesie procent
 * kosztu własnego (`bandPct`), a reguła zaczyna się już od nazwy pasma.
 */
export function bandSentence(margin: CandidateMargin, chipLabel: string): string {
  if (margin.bandPct !== undefined && margin.bandPct !== null) {
    return `Pasmo marży — ${margin.bandRule ?? chipLabel}; marża to ${pct(margin.bandPct)} kosztu własnego. To tylko oznaczenie — kolejność pojazdów się nie zmienia.`;
  }
  return `Alert marży: ${chipLabel}${margin.bandRule ? ` (${margin.bandRule})` : ''}. To tylko sygnał — kolejność pojazdów się nie zmienia.`;
}

/**
 * Marża przy kandydacie (01.10.2026) — trzecia linia pod dolotem i kosztem
 * dolotu: „marża ~X zł (Y %)” i chip pasma alertu, gdy progi są ustawione.
 *
 * Marża to szacunek (dolot ±8%, czas z modelu floty), więc stoi z „~”, a
 * rachunek jest w dymku. Brak składnika = „marża nieznana” z listą braków —
 * pusta komórka czytałaby się jak zero. Pasmo to alert, nie ranking:
 * kolejność kandydatów zostaje po dolocie, nic nie jest blokowane.
 *
 * `access` i `route` — dolot i trasa zlecenia z podpisanym źródłem
 * (06.10.2026): w dymku opłaty drogowe z HERE dla każdej z tras, a na końcu
 * zastrzeżenie, skąd są kilometry. Bez nich (starsze API) — jak dotąd.
 *
 * `wrap` — chip pasma może zejść pod kwotę, gdy kolumna jest wąska (karta
 * pojazdu, 06.10.2026: kwota z chipem „wyraźnie ujemna” w jednej linii to
 * ok. 260 px). Bez niego — w jednej linii, jak w rekomendacjach pojazdów.
 */
export const MarginCell = ({
  margin,
  access,
  route,
  wrap = false,
}: {
  margin?: CandidateMargin;
  access?: RouteFigure | null;
  route?: RouteFigure | null;
  wrap?: boolean;
}) => {
  // Koszt dolotu i trasy po składowych (15.10.2026, makieta FTL Etap 1).
  const details = useFeature(FEATURE_DETAILS);
  // Starsze API bez marży — nic nie dopisujemy pod kosztem.
  if (!margin) return null;
  const split = details ? costSplitSentences(margin.costSplit) : [];
  const sx = { whiteSpace: 'nowrap', display: 'block' } as const;
  if (margin.pln === null) {
    return (
      <Tooltip title={[`Brakuje danych do policzenia marży: ${margin.missing.join(', ')}.`, ...split].join(' ')}>
        <Typography variant="caption" color="text.secondary" sx={sx}>
          marża nieznana
        </Typography>
      </Tooltip>
    );
  }
  const chip = margin.band ? MARGIN_BAND_CHIP[margin.band] : null;
  // Rachunek z API (wyliczenie po średniku) jako jedno zdanie, dalej zdania
  // o alercie i szacunku — bez dawnego „….; Szacunek” na styku.
  const title = [
    margin.breakdown.length > 0 ? `${margin.breakdown.join('; ')}.` : null,
    ...split,
    ...tollsSentences(margin.breakdown, [
      { figure: access, where: 'na dolocie' },
      { figure: route, where: 'na trasie zlecenia' },
    ]),
    chip ? bandSentence(margin, chip.label) : NO_BAND_NOTE,
    marginCaveat(access, route),
  ]
    .filter((s): s is string => s !== null)
    .join(' ');
  return (
    <Tooltip title={title}>
      <Box
        data-testid="margin-line"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent: 'flex-end',
          ...(wrap ? { flexWrap: 'wrap', rowGap: 0.25 } : {}),
        }}
      >
        <Typography
          variant="caption"
          sx={wrap ? { display: 'block', textAlign: 'right' } : sx}
          color={margin.pln < 0 ? 'error.main' : undefined}
        >
          {/* Przy `wrap` łamie się tylko między kwotą a procentem. */}
          {wrap ? (
            <>
              {nb(`marża ~${pln(margin.pln)}`)}
              {margin.pct === null ? '' : ` ${nb(`(${pct(margin.pct)})`)}`}
            </>
          ) : (
            <>
              marża ~{pln(margin.pln)}
              {margin.pct === null ? '' : ` (${pct(margin.pct)})`}
            </>
          )}
        </Typography>
        {chip && (
          <Chip
            size="small"
            variant="outlined"
            color={chip.color}
            label={chip.label}
            data-testid="margin-band"
            sx={wrap ? WRAP_CHIP_SX : { height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
          />
        )}
      </Box>
    </Tooltip>
  );
};
